import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarPlus, Video, Pencil, Globe, Loader2, Users, Check, Film } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { AddToCalendar } from "./AddToCalendar";
import { RecapForm } from "./HearthShareCards";
import { RecordingNotice, RecordToggle } from "./RecordingPlaybook";

const toLocalInput = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fmtEventTime = (iso) => new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const eventPhase = (iso) => {
  const t = new Date(iso).getTime(), now = Date.now();
  if (now > t + 3 * 3600e3) return "ended";
  if (now >= t - 15 * 60e3) return "live";
  return "upcoming";
};

export const useNetText = (ct, iv) => {
  const { decryptNetText, netReady } = useCrypto();
  const [t, setT] = useState(null);
  useEffect(() => {
    let on = true;
    if (ct && netReady) decryptNetText(ct, iv).then((x) => on && setT(x || "")).catch(() => on && setT(""));
    return () => { on = false; };
  }, [ct, iv, decryptNetText, netReady]);
  return t;
};

// Owner form: create or edit a live session (title + Meet link encrypted with the network key).
export const HearthEventForm = ({ hearthId, post, initial, onDone }) => {
  const { hasNetwork, encryptNetText } = useCrypto();
  const [title, setTitle] = useState(initial?.title || "");
  const [link, setLink] = useState(initial?.link || "");
  const [details, setDetails] = useState(initial?.details || "");
  const [when, setWhen] = useState(post?.event ? toLocalInput(post.event.starts_at) : "");
  const [onBeacon, setOnBeacon] = useState(post?.event ? !!post.event.on_beacon : true);
  const [mayRecord, setMayRecord] = useState(!!post?.event?.may_record);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim() || !when) return toast.error("Add a title and a date & time");
    if (!/^https:\/\/\S+$/i.test(link.trim())) return toast.error("Add a meeting link starting with https://");
    if (!hasNetwork()) return toast.error("Unlock your vault first");
    setBusy(true);
    try {
      const [t, l] = await Promise.all([encryptNetText(title.trim()), encryptNetText(link.trim())]);
      const d = details.trim() ? await encryptNetText(details.trim()) : null;
      const body = { title_ct: t.ct, title_iv: t.iv, link_ct: l.ct, link_iv: l.iv, starts_at: new Date(when).toISOString(),
        on_beacon: onBeacon, may_record: mayRecord, ct: d?.ct || null, iv: d?.iv || null };
      if (post) await api.put(`/hearths/${hearthId}/events/${post.id}`, body);
      else await api.post(`/hearths/${hearthId}/events`, body);
      toast.success(post ? "Session updated" : "Live session scheduled, members were notified");
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the session"); } finally { setBusy(false); }
  };
  const inp = "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none";
  return (
    <div className="border border-[#14A89A]/40 bg-[#121212] p-4 my-4 space-y-2.5" data-testid="hearth-event-form">
      <div className="font-body text-xs uppercase tracking-wider text-[#14A89A] flex items-center gap-1.5"><CalendarPlus size={13} /> {post ? "Edit live session" : "Schedule a live session"}</div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Session title" data-testid="hearth-event-title" className={inp} />
      <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">Date & time
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="hearth-event-when"
          className={`${inp} mt-1 block min-w-0 max-w-full appearance-none [color-scheme:dark] min-h-[42px] normal-case tracking-normal`} />
      </label>
      <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Proton Meet link: https://meet.proton.me/…" data-testid="hearth-event-link" className={inp} />
      <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={2} maxLength={2000} placeholder="Details (optional)" data-testid="hearth-event-details" className={`${inp} resize-none`} />
      <label className="flex items-center gap-2 font-body text-xs text-zinc-300 cursor-pointer">
        <input type="checkbox" checked={onBeacon} onChange={(e) => setOnBeacon(e.target.checked)} data-testid="hearth-event-on-beacon" className="accent-[#14A89A]" />
        <Globe size={12} className="text-zinc-500" /> Also show on my Beacon page (title & time only, never the link)
      </label>
      <RecordToggle on={mayRecord} setOn={setMayRecord} testid="hearth-event-may-record" />
      <p className="font-body text-[11px] text-zinc-500">Members get a reminder 1 day before and about 15 minutes before.</p>
      <div className="flex justify-end gap-2">
        <button onClick={() => onDone(false)} data-testid="hearth-event-cancel" className="text-zinc-400 font-body text-xs px-3 py-1.5">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="hearth-event-save" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-1.5 font-body text-xs disabled:opacity-40 flex items-center gap-1.5">
          {busy && <Loader2 size={12} className="animate-spin" />}{post ? "Save" : "Schedule"}
        </button>
      </div>
    </div>
  );
};

// Beacon page: upcoming sessions the creator chose to show (title + time only; link stays in the Hearth).
const UpcomingRow = ({ ev, accent }) => {
  const title = useNetText(ev.title_ct, ev.title_iv);
  const link = useNetText(ev.link_ct, ev.link_iv);
  const live = eventPhase(ev.starts_at) === "live";
  const hearthUrl = `${window.location.origin}/hearth/${ev.hearth_id}`;
  const body = (
    <>
      <div className="min-w-0">
        <div className={`font-body text-sm text-white truncate ${ev.can_open ? "group-hover:underline" : ""}`}>{title ?? "·····"}</div>
        <div className="font-body text-xs text-zinc-400">{fmtEventTime(ev.starts_at)}</div>
      </div>
      <span className="font-body text-[10px] uppercase tracking-wider shrink-0" style={{ color: accent }}>{live ? "Live now" : ev.can_open ? "In the Hearth" : "For Hearth members"}</span>
    </>
  );
  const cls = "flex items-center justify-between gap-3 group";
  return (
    <div className="py-2.5 border-b border-white/5 last:border-0" data-testid={`beacon-event-${ev.id}`}>
      {ev.can_open ? <Link to={`/hearth/${ev.hearth_id}`} className={cls}>{body}</Link> : <div className={cls}>{body}</div>}
      {!live && title && <AddToCalendar ev={{ id: `b-${ev.id}`, uid: ev.id, title, startsAt: ev.starts_at, link: ev.can_open ? link : null, hearthUrl: ev.can_open ? hearthUrl : null }} />}
    </div>
  );
};

export const BeaconUpcoming = ({ events, accent }) => {
  const list = (events || []).filter((e) => eventPhase(e.starts_at) !== "ended");
  if (!list.length) return null;
  return (
    <div className="border border-white/10 bg-[#121212] px-4 py-3 mb-6" data-testid="beacon-upcoming-events">
      <div className="font-body text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1"><CalendarClock size={12} style={{ color: accent }} /> Upcoming live sessions</div>
      {list.map((e) => <UpcomingRow key={e.id} ev={e} accent={accent} />)}
    </div>
  );
};

export const HearthEventButton = ({ onClick }) => (
  <button onClick={onClick} data-testid="hearth-event-add" className="flex items-center gap-1.5 text-zinc-500 hover:text-[#14A89A] font-body text-xs my-2"><CalendarPlus size={13} /> Schedule a live session</button>
);

// "I'm going" for members; the owner sees only the count (never names).
const Rsvp = ({ hearthId, post, isOwner }) => {
  const [going, setGoing] = useState(!!post.event.going);
  const [busy, setBusy] = useState(false);
  if (isOwner) {
    const n = post.event.going_count || 0;
    return <div className="mt-2 font-body text-xs text-zinc-400 flex items-center gap-1.5" data-testid={`hearth-event-going-count-${post.id}`}><Users size={13} className="text-[#14A89A]" /> {n} {n === 1 ? "member is" : "members are"} going</div>;
  }
  const flip = async () => {
    setBusy(true);
    try { await api.post(`/hearths/${hearthId}/events/${post.id}/rsvp`, { going: !going }); setGoing(!going); if (!going) toast.success("You're going, see you there"); }
    catch { toast.error("Couldn't save, please try again"); } finally { setBusy(false); }
  };
  return (
    <button onClick={flip} disabled={busy} aria-pressed={going} data-testid={`hearth-event-rsvp-${post.id}`}
      className={`mt-2 inline-flex items-center gap-1.5 font-body text-xs px-3 py-1.5 transition-colors disabled:opacity-50 ${going ? "bg-[#14A89A]/15 text-[#14A89A] border border-[#14A89A]/50" : "border border-white/15 text-zinc-300 hover:border-[#14A89A]/50"}`}>
      {going ? <><Check size={13} /> I'm going</> : "I'm going?"}
    </button>
  );
};

// The event body inside a Hearth post card.
export const HearthEventCard = ({ hearthId, post, isOwner, onSaved }) => {
  const ev = post.event;
  const details = useNetText(post.ct, post.iv);
  const title = useNetText(ev.title_ct, ev.title_iv);
  const link = useNetText(ev.link_ct, ev.link_iv);
  const [editing, setEditing] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const phase = eventPhase(ev.starts_at);
  if (editing && title !== null && link !== null) {
    return <HearthEventForm hearthId={hearthId} post={post} initial={{ title, link, details: details || "" }} onDone={(ok) => { setEditing(false); if (ok) onSaved(); }} />;
  }
  return (
    <div className={`border p-3 mb-2 ${phase === "live" ? "border-[#14A89A] bg-[#14A89A]/10" : "border-white/10 bg-white/[0.02]"}`} data-testid={`hearth-event-${post.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-body text-[10px] uppercase tracking-wider text-[#14A89A] flex items-center gap-1 mb-1"><CalendarClock size={11} /> {phase === "live" ? "Live now" : phase === "ended" ? "Ended" : "Live session"}</div>
          <div className="font-body text-white font-medium break-words" data-testid={`hearth-event-title-${post.id}`}>{title ?? "·····"}</div>
          <div className="font-body text-xs text-zinc-400 mt-0.5" data-testid={`hearth-event-time-${post.id}`}>{fmtEventTime(ev.starts_at)}{ev.on_beacon && <span className="text-zinc-600"> · on Beacon</span>}</div>
        </div>
        {isOwner && <button onClick={() => setEditing(true)} data-testid={`hearth-event-edit-${post.id}`} title="Edit session" className="text-zinc-600 hover:text-[#14A89A] shrink-0"><Pencil size={13} /></button>}
      </div>
      {phase !== "ended" && ev.may_record && <RecordingNotice testid={`hearth-event-recording-${post.id}`} />}
      {phase !== "ended" && link && (
        <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`hearth-event-join-${post.id}`}
          className={`mt-3 inline-flex items-center gap-1.5 font-body text-xs px-3 py-1.5 ${phase === "live" ? "bg-[#14A89A] text-[#0A0A0A] font-semibold" : "border border-[#14A89A]/40 text-[#14A89A] hover:bg-[#14A89A]/10"}`}>
          <Video size={13} /> {phase === "live" ? "Join now" : "Meeting link"}
        </a>
      )}
      {phase === "upcoming" && title && <AddToCalendar ev={{ id: post.id, title, startsAt: ev.starts_at, link, details, hearthUrl: `${window.location.origin}/hearth/${hearthId}` }} />}
      {phase !== "ended" && <Rsvp hearthId={hearthId} post={post} isOwner={isOwner} />}
      {phase === "ended" && isOwner && (ev.recap_id
        ? <div className="mt-2 font-body text-xs text-zinc-500 flex items-center gap-1.5" data-testid={`hearth-event-recap-done-${post.id}`}><Check size={13} className="text-[#14A89A]" /> Recap posted</div>
        : recapOpen
          ? <RecapForm hearthId={hearthId} eventPost={post} title={title} onDone={(ok) => { setRecapOpen(false); if (ok) onSaved(); }} />
          : <button onClick={() => setRecapOpen(true)} data-testid={`hearth-event-recap-${post.id}`} className="mt-2 inline-flex items-center gap-1.5 border border-[#14A89A]/40 text-[#14A89A] font-body text-xs px-3 py-1.5 hover:bg-[#14A89A]/10"><Film size={13} /> Post recap</button>)}
    </div>
  );
};
